import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { DateInput } from '../components/ui/date-input';
import { toast } from 'sonner';
import { calcolaCodiceFiscale } from '../utils/fiscalCode';
import {
  Plus, Search, MoreHorizontal, FileCheck, Upload, Trash2, Pencil,
  FileText, ChevronLeft, ChevronRight, Sparkles, Loader2, UserPlus, Calculator, Printer,
  ArrowUp, ArrowDown,
} from 'lucide-react';
import { emptyClient, NAZIONI } from '../constants/clientConstants';
import ClientCompletionBanner from '../components/clients/ClientCompletionBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mappa il tipo documento scelto nel form sul document_type richiesto da /api/ocr/extract
const OCR_DOC_TYPE_MAP = {
  "Carta d'identità elettronica": 'carta_identita',
  "Carta d'identità cartacea": 'carta_identita',
  'Patente': 'patente',
  'Passaporto': 'passaporto',
  'Permesso di soggiorno': 'permesso_soggiorno',
};

// Etichetta breve con cui il documento caricato viene salvato tra i "Documenti anagrafici"
// del cliente (sezione Documenti di Clients.js), per riconoscerlo a colpo d'occhio.
const DOC_LABEL_MAP = {
  "Carta d'identità elettronica": 'CIE',
  "Carta d'identità cartacea": 'CIE',
  'Patente': 'PAT',
  'Passaporto': 'PASS',
  'Permesso di soggiorno': 'PSE',
};

const DOC_TIPO_OPTIONS = [
  "Carta d'identità elettronica",
  'Patente',
  'Passaporto',
  'Permesso di soggiorno',
  'Altro',
];

// Operatore del Patronato che firma il Mandato (backend: TEMPLATE_PER_OPERATORE in
// estratto_inps.py, stesso schema di Dimissioni).
const OPERATORE_OPTIONS = [
  { value: 'DING', label: 'Ding Yang Da Wei' },
  { value: 'MICHEL', label: 'Zhang Michel' },
];

const emptyForm = {
  client_id: '',
  doc_tipo: "Carta d'identità elettronica",
  doc_numero: '',
  doc_ente: '',
  cittadinanza: '',
  data_richiesta: '',
  operatore: 'DING',
  notes: '',
};

const EstrattoInps = () => {
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(window.location.search).get('cerca') || ''); // ?cerca= dal riepilogo cliente
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploadingDocFor, setUploadingDocFor] = useState(null);
  const [extractingDoc, setExtractingDoc] = useState(false);
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClientData, setNewClientData] = useState(emptyClient);
  const [creatingClient, setCreatingClient] = useState(false);
  // Documento d'identità caricato mentre il cliente non esiste ancora: va allegato in
  // anagrafica solo DOPO la creazione del cliente al salvataggio (vedi handleSubmit).
  const [pendingIdentityDoc, setPendingIdentityDoc] = useState(null); // { file, label }

  useEffect(() => { fetchClients(); }, []);

  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  // Nome del cliente nel campo di ricerca anche quando viene abbinato da un documento caricato
  // o si apre una pratica esistente (non solo quando lo si sceglie dall'elenco)
  useEffect(() => {
    const c = clients.find(x => x.id === formData.client_id);
    if (c) setClientSearch(`${c.last_name} ${c.first_name}`);
  }, [formData.client_id, clients]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchItems(); }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, currentPage, sortDirection]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento dei clienti');
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', currentPage);
      params.append('page_size', ITEMS_PER_PAGE);
      params.append('sort_direction', sortDirection);
      const res = await axios.get(`${API}/estratto-inps?${params.toString()}`);
      setItems(res.data.items);
      setTotalItems(res.data.total);
    } catch (e) {
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    const cognomeNome = `${c.last_name} ${c.first_name}`.toLowerCase();
    const nomeCognome = `${c.first_name} ${c.last_name}`.toLowerCase();
    return cognomeNome.includes(q) || nomeCognome.includes(q) || (c.fiscal_code || '').toLowerCase().includes(q);
  });

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const toggleSortDirection = () => {
    setCurrentPage(1);
    setSortDirection(d => (d === 'desc' ? 'asc' : 'desc'));
  };

  // Data di oggi (ora locale) in formato YYYY-MM-DD per l'input date
  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const openNew = () => {
    setEditingRecord(null);
    // Data richiesta = giorno di creazione, precompilata in automatico
    setFormData({ ...emptyForm, data_richiesta: todayIso() });
    setClientSearch('');
    setNewClientMode(false);
    setNewClientData(emptyClient);
    setPendingIdentityDoc(null);
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setNewClientMode(false);
    setNewClientData(emptyClient);
    setPendingIdentityDoc(null);
    setFormData({
      client_id: record.client_id || '',
      doc_tipo: record.doc_tipo || "Carta d'identità elettronica",
      doc_numero: record.doc_numero || '',
      doc_ente: record.doc_ente || '',
      cittadinanza: record.cittadinanza || '',
      data_richiesta: record.data_richiesta || '',
      operatore: record.operatore || 'DING',
      notes: record.notes || '',
    });
    setClientSearch('');
    setDialogOpen(true);
  };

  // Pulsante "Calcola CF" del form nuovo cliente (stesso di Anagrafica Clienti): calcola il CF
  // dai dati anagrafici e avvisa se appartiene già a un cliente registrato.
  const handleCalcolaCfDaDati = async () => {
    try {
      const cf = await calcolaCodiceFiscale(newClientData);
      setNewClientData(prev => ({ ...prev, fiscal_code: cf }));
      const existing = matchClientByCf(cf);
      if (existing) {
        toast.warning(`Attenzione: CF già registrato in anagrafica (${existing.last_name} ${existing.first_name})`);
      } else {
        toast.success('Codice Fiscale calcolato');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Prova ad abbinare un cliente esistente in anagrafica tramite codice fiscale estratto
  const matchClientByCf = (cf) => {
    if (!cf) return null;
    return clients.find(c => (c.fiscal_code || '').toUpperCase() === cf.toUpperCase()) || null;
  };

  // Allega il file caricato ai "Documenti anagrafici" del cliente (stessa sezione di
  // Clients.js), rinominato come "SIGLA COGNOME NOME" (es. "CIE ROSSI MARIO").
  // Se esiste già un documento dello stesso tipo (stessa sigla) in anagrafica, lo sostituisce
  // invece di aggiungerne un altro — la registrazione di un tipo documento va fatta una sola volta.
  const uploadClientDocument = async (clientId, file, shortLabel, lastName, firstName, expiryDate) => {
    try {
      const label = `${shortLabel} ${lastName || ''} ${firstName || ''}`.replace(/\s+/g, ' ').trim().toUpperCase();
      try {
        const clientRes = await axios.get(`${API}/clients/${clientId}`);
        const existing = (clientRes.data.documents || []).find(
          (d) => d.category === 'anagrafico' && (d.description || '').toUpperCase().startsWith(`${shortLabel} `)
        );
        if (existing) {
          await axios.delete(`${API}/clients/${clientId}/documents/${existing.id}`);
        }
      } catch (_) {
        // non bloccare l'upload se il controllo duplicati fallisce
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', label);
      fd.append('category', 'anagrafico');
      if (expiryDate) fd.append('expiry_date', expiryDate);
      await axios.post(`${API}/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } catch (error) {
      toast.error(`Dati estratti, ma il documento non è stato allegato in anagrafica: ${error.response?.data?.detail || 'errore'}`);
    }
  };

  const handleExtractIdentityDoc = async (file) => {
    if (!file) return;
    const ocrType = OCR_DOC_TYPE_MAP[formData.doc_tipo];
    if (!ocrType) {
      toast.error('Estrazione automatica non disponibile per "Altro" — seleziona il tipo documento corretto o inserisci i dati a mano');
      return;
    }
    const docLabel = DOC_LABEL_MAP[formData.doc_tipo] || ocrType.toUpperCase();
    setExtractingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', ocrType);

      // Estrae SEMPRE senza creare nulla in anagrafica: decidiamo noi in automatico se è un
      // cliente già esistente (abbiniamo per CF) o uno nuovo — in quel caso si passa da soli
      // alla modalità "Registra nuovo cliente" già precompilata, senza che l'operatore debba
      // attivarla a mano prima di caricare il documento.
      const res = await axios.post(`${API}/ocr/extract-client-data`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data.extracted_data || {};
      const c = res.data.client_data || {};
      const numero = d.document_number || d.license_number || '';
      const ente = d.issuing_authority || '';
      const cittad = d.nationality || '';
      const expiryDate = d.expiry_date || '';
      if (!numero && !ente && !cittad && !c.fiscal_code && !c.first_name) {
        toast.error('Nessun dato riconosciuto nel documento — verifica di aver scelto il "Tipo documento" corretto sopra prima di ricaricarlo');
        return;
      }
      setFormData(prev => ({
        ...prev,
        doc_numero: numero || prev.doc_numero,
        doc_ente: ente || prev.doc_ente,
        cittadinanza: cittad || prev.cittadinanza,
      }));

      const match = matchClientByCf(c.fiscal_code);
      if (match) {
        setNewClientMode(false);
        setFormData(prev => ({ ...prev, client_id: match.id }));
        await uploadClientDocument(match.id, file, docLabel, match.last_name, match.first_name, expiryDate);
        toast.success(`Cliente esistente abbinato: ${match.first_name} ${match.last_name} — documento allegato in anagrafica`);
      } else {
        // Unisce con quanto già nel form: un secondo documento caricato dopo il primo completa
        // i campi mancanti senza perdere quelli già trovati.
        let merged = {
          ...newClientData,
          first_name: c.first_name || newClientData.first_name,
          last_name: c.last_name || newClientData.last_name,
          fiscal_code: c.fiscal_code || newClientData.fiscal_code,
          gender: c.gender || newClientData.gender,
          birth_date: c.birth_date || newClientData.birth_date,
          birth_abroad: c.birth_abroad !== undefined ? c.birth_abroad : newClientData.birth_abroad,
          birth_comune: c.birth_comune || (c.birth_abroad ? '' : newClientData.birth_comune),
          birth_provincia: c.birth_provincia || newClientData.birth_provincia,
          birth_country: c.birth_country || newClientData.birth_country,
          address: c.address || newClientData.address,
          address_number: c.address_number || newClientData.address_number,
          city: c.city || newClientData.city,
          city_provincia: c.city_provincia || newClientData.city_provincia,
        };

        // CAP non riportato sul documento (le CIE italiane non lo stampano nell'indirizzo) —
        // se comune e provincia di residenza sono noti e individuano un solo CAP possibile,
        // lo compiliamo in automatico invece di lasciarlo vuoto.
        if (!merged.postal_code && merged.city && merged.city_provincia) {
          try {
            const capRes = await axios.get(`${API}/cap`, { params: { city: merged.city, provincia: merged.city_provincia } });
            if (capRes.data.single && capRes.data.auto_cap) {
              merged = { ...merged, postal_code: capRes.data.auto_cap };
            }
          } catch (e) {
            // il CAP è un aiuto in più: se il lookup fallisce non deve bloccare il resto del flusso
          }
        }

        // Il Passaporto (e spesso anche Patente/PSE) non riporta il CF italiano — se ora
        // abbiamo tutti i dati per calcolarlo (cognome, nome, sesso, nascita, luogo), lo
        // calcoliamo in automatico invece di lasciarlo vuoto.
        const canCalcCf = !merged.fiscal_code && merged.last_name && merged.first_name && merged.gender && merged.birth_date &&
          ((!merged.birth_abroad && merged.birth_comune && merged.birth_provincia) || (merged.birth_abroad && merged.birth_country));
        if (canCalcCf) {
          try {
            const cfRes = await axios.post(`${API}/calculate-fiscal-code`, {
              surname: merged.last_name,
              name: merged.first_name,
              birth_date: merged.birth_date,
              gender: merged.gender,
              birth_abroad: merged.birth_abroad,
              birth_comune: merged.birth_comune,
              birth_provincia: merged.birth_provincia,
              birth_country: merged.birth_country,
            });
            if (cfRes.data.fiscal_code) merged = { ...merged, fiscal_code: cfRes.data.fiscal_code };
          } catch (e) {
            // il calcolo CF è un aiuto in più: se fallisce non deve bloccare il resto del flusso
          }
        }

        // Il CF appena calcolato potrebbe corrispondere a un cliente già esistente — ricontrolla
        // per evitare di creare un duplicato.
        const matchAfterCalc = canCalcCf ? matchClientByCf(merged.fiscal_code) : null;
        if (matchAfterCalc) {
          setNewClientMode(false);
          setFormData(prev => ({ ...prev, client_id: matchAfterCalc.id }));
          await uploadClientDocument(matchAfterCalc.id, file, docLabel, matchAfterCalc.last_name, matchAfterCalc.first_name, expiryDate);
          toast.success(`Cliente esistente abbinato: ${matchAfterCalc.first_name} ${matchAfterCalc.last_name} — documento allegato in anagrafica`);
        } else {
          setNewClientMode(true);
          // Il cliente non esiste ancora: il documento si allega dopo la creazione (in handleSubmit).
          setPendingIdentityDoc({ file, label: docLabel, expiryDate });
          setNewClientData(merged);
          toast.success('Nuovo cliente rilevato — dati precompilati, rivedi e salva (il documento si allega automaticamente)');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'estrazione dati dal documento');
    } finally {
      setExtractingDoc(false);
    }
  };

  // Decodifica il CF (sesso, data e — quando riconosciuto — luogo di nascita: comune italiano
  // o nazione estera) e lo applica al form "Registra nuovo cliente". Il CF è "matematico" quindi
  // questi dati hanno priorità su quanto letto (magari in modo impreciso) dal documento OCR.
  const applyCfDecodeToNewClient = async (cfRaw) => {
    const cf = (cfRaw || '').trim().toUpperCase();
    if (cf.length < 16) return false;
    try {
      const res = await axios.post(`${API}/decode-fiscal-code`, { fiscal_code: cf });
      if (!res.data) return false;
      const bd = res.data.birth_date || '';
      const birthDateIso = /^\d{4}-\d{2}-\d{2}$/.test(bd)
        ? bd
        : (() => { const p = bd.split('/'); return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : ''; })();
      setNewClientData(prev => ({
        ...prev,
        gender: res.data.gender || prev.gender,
        birth_date: birthDateIso || prev.birth_date,
        ...(res.data.birth_abroad !== undefined ? {
          birth_abroad: res.data.birth_abroad,
          birth_country: res.data.birth_country || '',
          birth_comune: res.data.birth_comune || '',
          birth_provincia: res.data.birth_provincia || '',
        } : {}),
      }));
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleCalcolaCfNewClient = async () => {
    const cf = (newClientData.fiscal_code || '').trim().toUpperCase();
    if (cf.length < 16) {
      toast.error('Codice fiscale non valido (servono 16 caratteri)');
      return;
    }
    const ok = await applyCfDecodeToNewClient(cf);
    if (ok) {
      toast.success('Sesso, data e luogo di nascita calcolati dal Codice Fiscale');
    } else {
      toast.error('Impossibile calcolare i dati dal Codice Fiscale');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let clientId = formData.client_id;

    if (newClientMode) {
      if (!newClientData.last_name || !newClientData.first_name) {
        toast.error('Compila almeno Cognome e Nome del nuovo cliente');
        return;
      }
      if (!newClientData.birth_abroad && (!newClientData.birth_comune || !newClientData.birth_provincia)) {
        toast.error('Compila Comune e Provincia di nascita (o attiva "Nato/a all\'estero")');
        return;
      }
      if (newClientData.birth_abroad && !newClientData.birth_country) {
        toast.error('Seleziona la Nazione di nascita');
        return;
      }
      setCreatingClient(true);
      try {
        const res = await axios.post(`${API}/clients`, newClientData);
        clientId = res.data.id;
        if (pendingIdentityDoc) {
          await uploadClientDocument(clientId, pendingIdentityDoc.file, pendingIdentityDoc.label, newClientData.last_name, newClientData.first_name, pendingIdentityDoc.expiryDate);
          setPendingIdentityDoc(null);
        }
        toast.success('Nuovo cliente registrato in anagrafica');
        fetchClients();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Errore nella registrazione del nuovo cliente');
        setCreatingClient(false);
        return;
      }
      setCreatingClient(false);
    }

    if (!clientId) {
      toast.error('Seleziona il cliente');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, client_id: clientId };
      if (editingRecord) {
        await axios.put(`${API}/estratto-inps/${editingRecord.id}`, payload);
        toast.success('Pratica aggiornata');
      } else {
        await axios.post(`${API}/estratto-inps`, payload);
        toast.success('Pratica creata');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Eliminare la pratica di ${record.client_name}?`)) return;
    try {
      await axios.delete(`${API}/estratto-inps/${record.id}`);
      toast.success('Pratica eliminata');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const handleUploadDocumento = async (record, file) => {
    if (!file) return;
    setUploadingDocFor(record.id);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/estratto-inps/${record.id}/upload-documento`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Documento caricato');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del documento');
    } finally {
      setUploadingDocFor(null);
    }
  };

  const openPdf = async (url) => {
    try {
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch (error) {
      toast.error('Documento non disponibile');
    }
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  const uploadDocumentoInput = (record) => (
    <input
      type="file"
      accept="application/pdf,image/jpeg,image/png"
      className="hidden"
      disabled={uploadingDocFor === record.id}
      onChange={(e) => {
        const f = e.target.files?.[0];
        handleUploadDocumento(record, f);
        e.target.value = '';
      }}
    />
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estratto-inps-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Estratto Contributivo INPS
          </h1>
          <p className="text-slate-600 mt-1">Richiesta Estratto Contributivo tramite Patronato EPAS</p>
        </div>
        <Button onClick={openNew} data-testid="new-estratto-inps-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuova Pratica
        </Button>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per cliente o codice fiscale..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-estratto-inps"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : totalItems === 0 ? (
            <div className="p-8 text-center text-slate-500">Nessuna pratica trovata</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Data Richiesta</TableHead>
                    <TableHead className="font-semibold">
                      <button
                        type="button"
                        className="flex items-center gap-1 hover:text-slate-900"
                        onClick={toggleSortDirection}
                        title={sortDirection === 'desc' ? 'Più recenti prima' : 'Meno recenti prima'}
                      >
                        Data Caricamento
                        {sortDirection === 'desc' ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
                      </button>
                    </TableHead>
                    <TableHead className="font-semibold">Documento</TableHead>
                    <TableHead className="font-semibold">Stampa</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((record) => (
                    <TableRow key={record.id} data-testid={`estratto-inps-row-${record.id}`}>
                      <TableCell className="cursor-pointer hover:bg-slate-50" onClick={() => openEdit(record)}>
                        <div className="font-medium text-slate-900">{record.client_name || '-'}</div>
                        <div className="text-xs text-slate-500 font-mono">{record.client_fiscal_code}</div>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(record.data_richiesta)}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{formatDateTime(record.created_at)}</TableCell>
                      <TableCell>
                        {record.documento_filename ? (
                          <button
                            type="button"
                            onClick={() => openPdf(`${API}/estratto-inps/${record.id}/documento`)}
                            title={record.documento_original_name}
                          >
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
                              <FileCheck className="w-3 h-3 mr-1" />Caricato
                            </Badge>
                          </button>
                        ) : null}
                        {record.documento_filename ? (
                          <label className="ml-1.5 inline-flex items-center text-slate-400 hover:text-slate-700 cursor-pointer align-middle" title="Sostituisci documento">
                            {uploadingDocFor === record.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                            {uploadDocumentoInput(record)}
                          </label>
                        ) : (
                          <label className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 cursor-pointer border border-dashed border-slate-300 rounded-full px-2.5 py-1">
                            {uploadingDocFor === record.id ? (
                              'Caricamento...'
                            ) : (
                              <>
                                <Upload className="w-3 h-3" /> Carica
                              </>
                            )}
                            {uploadDocumentoInput(record)}
                          </label>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openPdf(`${API}/estratto-inps/${record.id}/mandato-pdf`)}
                            title="Mandato Patronato + Consenso"
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openPdf(`${API}/estratto-inps/${record.id}/tutti-documenti-pdf`)}
                            title="Stampa tutto: Mandato + Consenso + documento d'identità + documento caricato"
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(record)}>
                              <Pencil className="w-4 h-4 mr-2" />Modifica
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(record)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                  <p className="text-sm text-slate-500">
                    Visualizzando {startIndex + 1}-{Math.min(endIndex, totalItems)} di {totalItems} pratiche
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600">Pagina {currentPage} di {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Modifica Pratica' : 'Nuova Pratica Estratto Contributivo INPS'}</DialogTitle>
            <DialogDescription>
              I dati anagrafici del cliente (nascita, residenza) vengono presi automaticamente
              dall'anagrafica per compilare il Mandato Patronato.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-violet-800 font-medium text-sm">
                <Sparkles className="w-4 h-4" />
                Estrazione automatica dati (facoltativa)
              </div>
              <p className="text-xs text-violet-700">
                Carica il documento d'identità: i campi sotto si compilano da soli (puoi comunque
                correggerli a mano).
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs text-violet-700">Tipo documento d'identità che stai per caricare</Label>
                <Select value={formData.doc_tipo} onValueChange={(v) => setFormData({ ...formData, doc_tipo: v })}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className={`flex items-center justify-center gap-2 border border-dashed border-violet-300 rounded-lg py-2.5 text-sm text-violet-700 hover:bg-violet-100 cursor-pointer ${extractingDoc ? 'opacity-60 pointer-events-none' : ''}`}>
                {extractingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Carica {formData.doc_tipo}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    handleExtractIdentityDoc(f);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {/* Avviso anagrafica incompleta per il cliente selezionato (a mano o abbinato da documento) */}
            <ClientCompletionBanner
              client={newClientMode ? null : clients.find(c => c.id === formData.client_id)}
              onSaved={(updated) => setClients(prev => prev.map(c => c.id === updated.id ? updated : c))}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Cliente</Label>
                {!editingRecord && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-xs gap-1"
                    onClick={() => setNewClientMode(m => !m)}
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    {newClientMode ? 'Seleziona cliente esistente' : 'Nuovo cliente'}
                  </Button>
                )}
              </div>

              {!newClientMode ? (
                <div className="relative">
                  {/* Ricerca cliente come in Canone RAI: un solo campo, elenco a tendina solo mentre si cerca */}
                  <Input
                    placeholder="Cerca cliente per nome o codice fiscale..."
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                    disabled={!!editingRecord}
                    data-testid="estratto-inps-client-search"
                  />
                  {showClientDropdown && !editingRecord && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 border rounded-lg max-h-48 overflow-y-auto bg-white shadow-lg">
                      {filteredClients.length === 0 ? (
                        <div className="p-3 text-center text-slate-500 text-sm">Nessun cliente trovato</div>
                      ) : (
                        filteredClients.slice(0, 10).map((c) => (
                          <div
                            key={c.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setFormData(prev => ({ ...prev, client_id: c.id }));
                              setClientSearch(`${c.last_name} ${c.first_name}`);
                              setShowClientDropdown(false);
                            }}
                            className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-b-0 ${formData.client_id === c.id ? 'bg-blue-50 border-blue-200' : ''}`}
                          >
                            <div className="font-medium">{c.last_name} {c.first_name}</div>
                            <div className="text-sm text-slate-500 font-mono">{c.fiscal_code}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {formData.client_id && (
                    <p className="text-sm text-green-600 mt-2">Cliente selezionato</p>
                  )}
                </div>
              ) : (
                <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                  <p className="text-xs text-slate-500">
                    Il cliente non è ancora in anagrafica: compila i dati per registrarlo — verrà creato
                    al salvataggio della pratica.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Cognome</Label>
                      <Input value={newClientData.last_name}
                        onChange={(e) => setNewClientData({ ...newClientData, last_name: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Nome</Label>
                      <Input value={newClientData.first_name}
                        onChange={(e) => setNewClientData({ ...newClientData, first_name: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Sesso</Label>
                      <Select value={newClientData.gender} onValueChange={(v) => setNewClientData({ ...newClientData, gender: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">Maschio</SelectItem>
                          <SelectItem value="F">Femmina</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Data di nascita</Label>
                      <DateInput value={newClientData.birth_date}
                        onChange={(val) => setNewClientData({ ...newClientData, birth_date: val })}
                        placeholder="gg/mm/aaaa" />
                    </div>
                  </div>

                  <div className="p-3 bg-white border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-slate-500">Nato/a all'estero</Label>
                      <Switch
                        checked={newClientData.birth_abroad}
                        onCheckedChange={(checked) => setNewClientData({
                          ...newClientData, birth_abroad: checked, birth_comune: '', birth_provincia: '', birth_country: checked ? 'Cina' : '',
                        })}
                      />
                    </div>
                    {!newClientData.birth_abroad ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-500">Comune di nascita</Label>
                          <Input value={newClientData.birth_comune}
                            onChange={(e) => setNewClientData({ ...newClientData, birth_comune: e.target.value.toUpperCase() })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-slate-500">Provincia (sigla)</Label>
                          <Input value={newClientData.birth_provincia} maxLength={2} className="uppercase"
                            onChange={(e) => setNewClientData({ ...newClientData, birth_provincia: e.target.value.toUpperCase() })} />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <Label className="text-xs text-slate-500">Nazione di nascita</Label>
                        <Select value={newClientData.birth_country} onValueChange={(v) => setNewClientData({ ...newClientData, birth_country: v })}>
                          <SelectTrigger><SelectValue placeholder="Seleziona nazione" /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {NAZIONI.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Codice fiscale</Label>
                    <div className="flex gap-2">
                      <Input value={newClientData.fiscal_code} className="uppercase"
                        onChange={(e) => setNewClientData({ ...newClientData, fiscal_code: e.target.value.toUpperCase() })} />
                      <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfDaDati}>Calcola CF</Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleCalcolaCfNewClient} title="Calcola sesso e data di nascita dal CF">
                        <Calculator className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Indirizzo</Label>
                      <Input value={newClientData.address}
                        onChange={(e) => setNewClientData({ ...newClientData, address: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Numero civico</Label>
                      <Input value={newClientData.address_number}
                        onChange={(e) => setNewClientData({ ...newClientData, address_number: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-slate-500">Città di residenza</Label>
                      <Input value={newClientData.city}
                        onChange={(e) => setNewClientData({ ...newClientData, city: e.target.value.toUpperCase() })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">Provincia</Label>
                      <Input value={newClientData.city_provincia} maxLength={2} className="uppercase"
                        onChange={(e) => setNewClientData({ ...newClientData, city_provincia: e.target.value.toUpperCase() })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-500">CAP</Label>
                      <Input value={newClientData.postal_code}
                        onChange={(e) => setNewClientData({ ...newClientData, postal_code: e.target.value })} />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-slate-500">Cellulare</Label>
                      <Input value={newClientData.phone}
                        onChange={(e) => setNewClientData({ ...newClientData, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input type="email" value={newClientData.email}
                      onChange={(e) => setNewClientData({ ...newClientData, email: e.target.value.toUpperCase() })} />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Documento d'identità</Label>
                <Select value={formData.doc_tipo} onValueChange={(v) => setFormData({ ...formData, doc_tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DOC_TIPO_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cittadinanza</Label>
                <Input value={formData.cittadinanza} onChange={(e) => setFormData({ ...formData, cittadinanza: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-2">
                <Label>Numero documento</Label>
                <Input value={formData.doc_numero} onChange={(e) => setFormData({ ...formData, doc_numero: e.target.value.toUpperCase() })} />
              </div>
              <div className="space-y-2">
                <Label>Rilasciato da</Label>
                <Input value={formData.doc_ente} onChange={(e) => setFormData({ ...formData, doc_ente: e.target.value.toUpperCase() })} />
              </div>
            </div>

            <div className="border-t pt-4 grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data richiesta</Label>
                <Input type="date" value={formData.data_richiesta} onChange={(e) => setFormData({ ...formData, data_richiesta: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Operatore (firma Mandato)</Label>
                <Select value={formData.operatore} onValueChange={(v) => setFormData({ ...formData, operatore: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPERATORE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Note</Label>
                <Textarea rows={3} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving || creatingClient}>
                {creatingClient ? 'Registrazione cliente...' : saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstrattoInps;
